<template>
  <div class="appointment-confirmation">
    <div class="confirmation-message">
      <h3 class="confirmation-title body20-bold">
        {{ $t('appointment.confirmation_title', { store: appointment.store.name }) }}
      </h3>

      <p class="confirmation-instructions body20-light">
        {{ $t('appointment.confirmation_instructions') }}
      </p>
    </div>

    <div class="appointment-details">
      <h3 class="details-title headline50">{{ $t('appointment.details_title') }}</h3>

      <div class="details-grid">
        <div class="detail-item">
          <div class="body20-bold purpose-title">{{ $t('appointment.purpose_label') }}</div>
          <div class="body20-light purpose-content">{{ appointment.purpose.name }}</div>
        </div>

        <div class="detail-item">
          <div class="body20-bold date-title">{{ $t('appointment.date_label') }}</div>
          <div class="body20-light date-content">{{ formattedDate }}</div>
        </div>

        <div class="detail-item">
          <div class="body20-bold location-title">{{ $t('appointment.location_label') }}</div>
          <div class="body20-light location-content">{{ appointment.store.address }}</div>
        </div>
      </div>
    </div>

    <div class="confirmation-actions">
      <div class="action-item">
        <button class="btn-add-calendar" @click="showCalendarDialog = true" :disabled="isLoading">
          <img :src="calendarIcon" alt="Icona calendario">
          <span class="legend16-bold">
            {{ $t('appointment.add_to_calendar') }}
          </span>
        </button>
      </div>

      <div class="action-item">
        <div class="notification-option checkbox-wrapper">
          <input type="checkbox" id="notification-checkbox" class="form-check-input" v-model="notifyEarlier"
            @change="handleNotificationChange" :disabled="!isAppointmentIdAvailable || isLoading" />
          <label for="notification-checkbox" class="form-check-label legend14-bold"
            :class="{ 'disabled-label': !isAppointmentIdAvailable || isLoading }">
            {{ $t('appointment.notify_earlier') }}
          </label>
        </div>
        <div v-if="notificationStatus || !isAppointmentIdAvailable || isLoading"
          class="notification-status legend16-light" :class="notificationStatusClass">
          {{
            isLoading
              ? $t('appointment.saving_appointment')
              : isAppointmentIdAvailable
                ? notificationStatus
                : $t('appointment.creating_appointment')
          }}
        </div>
      </div>

      <div class="action-buttons">
        <button class="btn-homepage primary-cta" @click="goToHomepage" :disabled="isLoading"
          :class="{ 'btn-disabled': isLoading }">
          {{ $t('common.return_to_home') }}
        </button>
        <button class="btn-modify primary-cta" @click="modifyAppointment" :disabled="isLoading"
          :class="{ 'btn-disabled': isLoading }">
          {{ $t('appointment.modify_appointment') }}
        </button>
      </div>
    </div>

    <!-- Calendar Picker Dialog -->
    <calendar-picker-dialog :is-visible="showCalendarDialog" @close="showCalendarDialog = false"
      @select-calendar="addToCalendar" />
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import CalendarPickerDialog from "./common/CalendarPickerDialog.vue";
import calendarIcon from "@/assets/images/calendar-green-icon.svg";

export default {
  name: "AppointmentConfirmation",

  components: {
    CalendarPickerDialog,
  },

  props: {
    appointment: {
      type: Object,
      required: true,
    },
  },

  data () {
    return {
      notifyEarlier: false,
      notificationStatus: "",
      notificationStatusClass: "",
      showCalendarDialog: false,
      calendarIcon
    };
  },

  computed: {
    ...mapState({
      // Map the loading state from the Vuex store
      appointmentLoading: state => state.appointment.loading,
    }),

    isLoading () {
      // Return true if appointment is being saved or if appointment ID is not available yet
      return this.appointmentLoading || !this.isAppointmentIdAvailable;
    },

    formattedDate () {
      if (!this.appointment.date) return "";

      // Estrai solo l'orario di inizio (prima parte di "09:30-10:00")
      const startTime = this.appointment.time.split("-")[0];

      const date = new Date(`${this.appointment.date}T${startTime}`);

      const options = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };

      // Usa la locale corrente dell'applicazione
      const locale = this.$i18n ? this.$i18n.locale : 'it-IT';
      return new Intl.DateTimeFormat(locale, options).format(date);
    },

    isAppointmentIdAvailable () {
      // Check if appointment object exists and has an id property
      return this.appointment && this.appointment.id;
    },
  },

  watch: {
    // Osserva l'oggetto appointment per reagire a eventuali cambiamenti
    appointment: {
      handler: "initializeNotificationPreference",
      immediate: true,
      deep: true,
    },
  },

  mounted () {
    // Inizializza lo stato della checkbox
    this.initializeNotificationPreference();
  },

  methods: {
    ...mapActions({
      setNotificationPreference: "appointment/setEarlierNotificationPreference",
    }),

    initializeNotificationPreference () {
      // Verifica se l'appointment è disponibile e contiene la proprietà notifyEarlier
      if (this.appointment) {
        // Assegna il valore di notifyEarlier (o false se non definito)
        if (this.appointment.notifyEarlier !== undefined) {
          this.notifyEarlier = Boolean(this.appointment.notifyEarlier);

          // Se la notifica è attiva, mostra il messaggio di stato appropriato
          if (this.notifyEarlier) {
            this.notificationStatus = this.$t('appointment.notification_active');
            this.notificationStatusClass = "status-success";
          } else {
            this.notificationStatus = "";
            this.notificationStatusClass = "";
          }
        }
      }
    },

    addToCalendar (type) {
      // Don't allow adding to calendar while loading
      if (this.isLoading) return;
      this.$emit("add-to-calendar", type);
    },

    modifyAppointment () {
      // Don't allow modifying while loading
      if (this.isLoading) return;
      this.$emit("modify-appointment");
    },

    goToHomepage () {
      // Don't allow navigating home while loading
      if (this.isLoading) return;
      window.location.href = "/";
    },

    async handleNotificationChange () {
      try {
        // Don't handle notification change if loading
        if (this.isLoading) {
          // Revert the checkbox state if we're still loading
          this.notifyEarlier = !this.notifyEarlier;
          return;
        }

        // Mostra lo stato in corso
        this.notificationStatus = this.$t('appointment.updating_status');
        this.notificationStatusClass = "status-pending";

        // Ottieni l'ID dell'appuntamento
        const appointmentId = this.appointment.id;

        if (!appointmentId) {
          console.error(this.$t('errors.appointment_id_not_found'), this.appointment);
          throw new Error(this.$t('errors.appointment_id_unavailable'));
        }

        // Utilizza l'azione Vuex per aggiornare la preferenza
        const notificationPreference = await this.setNotificationPreference({
          appointmentId: appointmentId,
          enableNotification: this.notifyEarlier,
        });

        this.notifyEarlier = notificationPreference.notify_earlier;

        // Aggiorna il messaggio di stato
        if (this.notifyEarlier) {
          this.notificationStatus = this.$t('appointment.notification_active');
          this.notificationStatusClass = "status-success";
        } else {
          this.notificationStatus = this.$t('appointment.notification_disabled');
          this.notificationStatusClass = "status-info";

          // nasconde il messaggio dopo 3 secondi se la notifica è disattivata
          setTimeout(() => {
            if (!this.notifyEarlier) {
              this.notificationStatus = "";
            }
          }, 3000);
        }
      } catch (error) {
        console.error(this.$t('errors.notification_update_error'), error);
        this.notificationStatus = this.$t('errors.general');
        this.notificationStatusClass = "status-error";

        // Ripristina lo stato precedente della checkbox
        this.notifyEarlier = !this.notifyEarlier;
      }
    },
  },
};
</script>

<style scoped>
.notification-status {
  padding: 10px;
  margin-top: 40px;
}

.status-pending {
  background-color: #f8f9fa;
  color: #6c757d;
}

.status-success {
  background-color: #d4edda;
  color: #155724;
}

.status-error {
  background-color: #f8d7da;
  color: #721c24;
}

.status-info {
  background-color: #e2e3e5;
  color: #383d41;
}

.disabled-label {
  color: #6c757d;
  cursor: not-allowed;
}

input[type="checkbox"]:disabled+label {
  cursor: not-allowed;
}

.btn-disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>